/**
 * batch-62 klant-vaste-inzet (FO-458) - de vaste inzet-kring van een klant.
 *
 * De klant-afwezigheidsweergave leidde haar personen af uit ALLE dagregels van
 * de klant: een invaller van één dag stond er net zo prominent in als de vaste
 * bezetting van een half jaar. De kring beperkt de PERSONEN tot de vaste inzet:
 *
 *   vaste monteurs (FO-440-registratie, bevestigd of voorlopig-afgeleid)
 *   ∪ lopend ingepland in het venster (geplande uren boven de Regelmatig-grens)
 *   ∪ ervaringscategorie >= REGELMATIG (FO-413-D5, klant-eigen grenzen)
 *   minus handmatige uitsluitingen, plus handmatige toevoegingen (relatie-tab
 *   Klantenportaal, met reden en audit).
 *
 * Wie buiten de kring valt en toch uitvalt op een ingeplande dienst, verschijnt
 * niet als naam maar als dienst met een gat ("vervanging wordt ingepland").
 *
 * De PURE beslissing staat in `@streep/utils` (`vaste-inzet-kring.ts`); hier de
 * string-unions, labels, DTO's en het gedeelde request-schema (één schema voor
 * API en formulier, CLAUDE.md § Validatie).
 */
import { z } from 'zod'

// ─── Rol, gronden, status ────────────────────────────────────────────────────

/**
 * De rol die de KLANT leest. "Vaste monteur" is uitsluitend een door de planner
 * bevestigde FO-440-koppeling (één waarheid met de weekplanning, waar alleen een
 * bevestigde koppeling als vaste monteur verschijnt). Alle andere leden van de
 * kring - voorlopig afgeleid, lopend ingepland, op uren regelmatig of ingewerkt,
 * of handmatig toegevoegd - lezen als "regelmatig ingezet".
 */
export const KLANT_VASTE_INZET_ROLLEN = ['VASTE_MONTEUR', 'REGELMATIG'] as const
export type KlantVasteInzetRol = (typeof KLANT_VASTE_INZET_ROLLEN)[number]

/** Waarom iemand (automatisch of handmatig) tot de kring behoort. */
export const KLANT_VASTE_INZET_GRONDEN = [
  'VASTE_MONTEUR',
  'VASTE_MONTEUR_VOORLOPIG',
  'LOPENDE_PLANNING',
  'ERVARING',
  'HANDMATIG',
] as const
export type KlantVasteInzetGrond = (typeof KLANT_VASTE_INZET_GRONDEN)[number]

/** De handmatige uitzondering (Prisma-enum `KlantVasteInzetUitzondering`). */
export const KLANT_VASTE_INZET_UITZONDERINGEN = ['TOEGEVOEGD', 'UITGESLOTEN'] as const
export type KlantVasteInzetUitzondering = (typeof KLANT_VASTE_INZET_UITZONDERINGEN)[number]

/**
 * Eindoordeel per kandidaat:
 *   IN_KRING        telt mee; afwezigheid verschijnt met naam en rol
 *   UITGESLOTEN     handmatig uitgesloten (reden in beheer); diensten blijven gewoon zichtbaar
 *   NIET_ZICHTBAAR  zou meetellen, maar is voor deze klant niet zichtbaar (strengste wint)
 *   BUITEN_KRING    alleen incidentele inzet
 */
export const KLANT_VASTE_INZET_STATUSSEN = [
  'IN_KRING',
  'UITGESLOTEN',
  'NIET_ZICHTBAAR',
  'BUITEN_KRING',
] as const
export type KlantVasteInzetStatus = (typeof KLANT_VASTE_INZET_STATUSSEN)[number]

/** Waarom een kandidaat niet klant-zichtbaar is (beheer-uitleg). */
export const KLANT_VASTE_INZET_NIET_ZICHTBAAR_REDENEN = [
  'GLOBAAL_VERBORGEN',
  'VERBORGEN_VOOR_KLANT',
  'VERTROKKEN',
] as const
export type KlantVasteInzetNietZichtbaarReden =
  (typeof KLANT_VASTE_INZET_NIET_ZICHTBAAR_REDENEN)[number]

// ─── Backoffice-labels (de klant-app vertaalt zelf via next-intl) ────────────

export const KLANT_VASTE_INZET_ROL_LABELS: Record<KlantVasteInzetRol, string> = {
  VASTE_MONTEUR: 'Vaste monteur',
  REGELMATIG: 'Regelmatig ingezet',
}

export const KLANT_VASTE_INZET_GROND_LABELS: Record<KlantVasteInzetGrond, string> = {
  VASTE_MONTEUR: 'Vaste monteur (bevestigd)',
  VASTE_MONTEUR_VOORLOPIG: 'Vaste monteur (voorlopig, nog niet bevestigd)',
  LOPENDE_PLANNING: 'Lopend ingepland',
  ERVARING: 'Regelmatig of ingewerkt (uren bij deze klant)',
  HANDMATIG: 'Handmatig toegevoegd',
}

export const KLANT_VASTE_INZET_STATUS_LABELS: Record<KlantVasteInzetStatus, string> = {
  IN_KRING: 'Vaste inzet',
  UITGESLOTEN: 'Uitgesloten',
  NIET_ZICHTBAAR: 'Niet zichtbaar voor de klant',
  BUITEN_KRING: 'Geen vaste inzet',
}

export const KLANT_VASTE_INZET_NIET_ZICHTBAAR_LABELS: Record<
  KlantVasteInzetNietZichtbaarReden,
  string
> = {
  GLOBAAL_VERBORGEN: 'staat voor alle klanten verborgen (medewerkerkaart)',
  VERBORGEN_VOOR_KLANT: 'is voor deze klant verborgen (blok hieronder)',
  VERTROKKEN: 'is uit dienst',
}

// ─── Beheer: handmatige uitzondering (relatie-tab Klantenportaal) ────────────

export const KLANT_VASTE_INZET_ACTIES = ['TOEVOEGEN', 'UITSLUITEN', 'AUTOMATISCH'] as const
export type KlantVasteInzetActie = (typeof KLANT_VASTE_INZET_ACTIES)[number]

/**
 * TOEVOEGEN / UITSLUITEN vragen een reden (min. 3 tekens, zichtbaar in beheer
 * en audit). AUTOMATISCH zet de uitzondering terug naar de automatische kring;
 * de reden is daar optioneel.
 */
export const klantVasteInzetUitzonderingSchema = z
  .object({
    employeeId: z.string().trim().min(1, 'Kies een monteur'),
    actie: z.enum(KLANT_VASTE_INZET_ACTIES),
    reden: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
  })
  .refine((v) => v.actie === 'AUTOMATISCH' || (v.reden ?? '').trim().length >= 3, {
    message: 'Geef een reden van minimaal 3 tekens',
    path: ['reden'],
  })
export type KlantVasteInzetUitzonderingInput = z.infer<typeof klantVasteInzetUitzonderingSchema>

/** Eén kandidaat in het beheer-overzicht "wie geldt als vaste inzet". */
export interface KlantVasteInzetBeheerRij {
  employeeId: string
  naam: string
  functionTitle: string | null
  status: KlantVasteInzetStatus
  /** Alleen bij IN_KRING. */
  rol: KlantVasteInzetRol | null
  gronden: KlantVasteInzetGrond[]
  /** Geplande uren bij deze klant in het venster (de "lopend ingepland"-toets). */
  geplandeUren: number
  /** Aantal geplande dagen bij deze klant in het venster. */
  geplandeDagen: number
  /** Cumulatieve gewerkte uren bij deze klant (D5-cache); `null` zonder telling. */
  gewerkteUren: number | null
  ervaring: 'INCIDENTEEL' | 'REGELMATIG' | 'INGEWERKT' | null
  nietZichtbaarReden: KlantVasteInzetNietZichtbaarReden | null
  uitzondering: {
    soort: KlantVasteInzetUitzondering
    reden: string | null
    doorNaam: string | null
    op: string | null
  } | null
}

export interface KlantVasteInzetBeheerDTO {
  /** `yyyy-MM-dd`; het venster waarop "lopend ingepland" rekent. */
  venster: { van: string; tot: string }
  /** De Regelmatig-grens (uren) van deze klant. */
  grensRegelmatigUren: number
  rijen: KlantVasteInzetBeheerRij[]
}

// ─── Klant-facing vormen (nooit de reden van afwezigheid) ────────────────────

/** Een aaneengesloten afwezige periode met dezelfde vervangingsstatus. */
export interface KlantAfwezigePeriode {
  /** `yyyy-MM-dd` */
  van: string
  /** `yyyy-MM-dd`, inclusief */
  tot: string
  aantalDagen: number
  vervangerNaam: string | null
  wordtIngepland: boolean
  vervangingViaPlanning: boolean
}

/**
 * Een dienst waarvan de ingeplande (incidentele) monteur uitvalt: de klant ziet
 * het gat, niet de naam van iemand die hij niet kent (spec §3.2).
 */
export interface KlantDienstGat {
  /** `yyyy-MM-dd` */
  datum: string
  /** Ruwe wandklok-tijden van de dagregel (`HH:mm`), zoals op het planbord. */
  startTijd: string | null
  eindTijd: string | null
  locatie: string | null
  /** Aantal diensten met hetzelfde gat (zelfde dag, tijden en locatie). */
  aantal: number
}
